import React, { useState, useRef } from 'react';
import type { WordConfidence } from '../speech';
import type { LessonPart } from './useLessonApi';
import type { EarlierPhrase } from '../turncard/rail';

/**
 * The help a phrase has earned from misses and asks (BR-051, BR-052). Each level adds to the last: 1 the meaning and
 * the phrase's clip (`audioFormId`; `audioUrls` once fetched), 2 the start of the phrase, 3 the phrase itself.
 */
export interface HelpLadder {
  level: number;
  meaning: string | null;
  audioFormId: string | null;
  audioUrls?: { natural: string; slow?: string };
  cue: string | null;
  phrase: string | null;
}

export interface Message {
  id: string;
  sender: 'tutor' | 'user';
  content: string;
  /** Short usage note shown under the content (an introduction's explanation of a persona line). */
  note?: string;
  /** What a lesson persona line means, sent with its clip so the card shows it at once (BR-042). */
  meaning?: string;
  /** Two-to-five-word gloss of the line from the explanation (#341); carried for the side rail, not shown here. */
  gloss?: string;
  /** Fixed label shown above a tutor prompt (e.g. "Your choice" when the learner may pick any phrase). */
  label?: string;
  /** Target-language text for the phrase being modelled (language-agnostic). */
  targetText?: string;
  timestamp?: string;
  audioUrls?: {
    natural: string;
    slow?: string;
  };
  /** Form whose clips a play_audio message models (for re-fetching them). */
  audioFormId?: string;
  /**
   * Set on a play_audio message with no playable clip: its URLs failed to
   * load (`fetch_failed`, retryable) or the step names no form (`missing`).
   */
  audioUnavailable?: 'fetch_failed' | 'missing';
  /**
   * Per-word confidence for a user-transcript message, when the STT provider
   * supplied it (GCP only — see {@link WordConfidence}). Used to color-code
   * low-confidence words in the feedback; absent for tutor messages and
   * for transcripts from providers without word-level data.
   */
  words?: WordConfidence[];
  /**
   * Check-in persona line (BR-043): audio only. Never render its text or offer
   * slow audio; the text is kept in `audioText` solely to re-fetch the clip.
   */
  audioOnly?: boolean;
  /** Text and locale to re-synthesise an audio-only line (never rendered). */
  audioText?: string;
  audioLocale?: string;
  /** Speed of the clip in `audioUrls.natural`, for re-fetching it. */
  audioSpeed?: 'natural' | 'slow';
  /**
   * A lesson play_audio line spoken by the character (`purpose:
   * persona_utterance`), which the "?" help explains. Model phrases the
   * learner is to say carry no flag. Check-in lines are `audioOnly`.
   */
  persona?: boolean;
  /** A request_production cue (`prompted` or `known`): shown as a chip above the mic. */
  cue?: boolean;
  /** Play the clip as soon as it is on screen (check-in persona lines). */
  autoPlay?: boolean;
  /** The step's action_type that produced this message (e.g. 'play_audio'). */
  stepKind?: string;
  /** The lesson part the step belongs to (`content.part_id`); null/absent when none. */
  partId?: string | null;
  /** On a give_feedback message: whether the attempt was accepted. */
  success?: boolean;
  /** On a request_production or retry give_feedback message: the help the phrase has earned (BR-051). */
  ladder?: HelpLadder;
}

export interface LessonState {
  messages: Message[];
  contextName: string | null;
  contextId: string | null;
  languageCode: string | null;
  loading: boolean;
  processing: boolean;
  error: string | null;
  runId: string | null;
  stepVersion: number | null;
  /** Index of the current step; sent with mic uploads for telemetry (TR-037). */
  stepIndex: number | null;
  lessonComplete: boolean;
  phraseHints: string[] | null;
  /** True while the run is waiting for the learner to speak (enables the mic). */
  awaitingInput: boolean;
  /** True when an automatic advance failed and the learner can retry it (#250). */
  advanceFailed: boolean;
}

export const useLessonState = (): {
  // State
  messages: Message[];
  /** Phrases learned before a resume, for the side rail (#347); not chat messages. */
  earlierPhrases: EarlierPhrase[];
  /** The required parts the run covers, in script order (empty for practice). */
  parts: LessonPart[];
  contextName: string | null;
  contextId: string | null;
  languageCode: string | null;
  loading: boolean;
  processing: boolean;
  error: string | null;
  runId: string | null;
  stepVersion: number | null;
  /** Index of the current step; sent with mic uploads for telemetry (TR-037). */
  stepIndex: number | null;
  lessonComplete: boolean;
  phraseHints: string[] | null;
  awaitingInput: boolean;
  advanceFailed: boolean;
  // Refs
  isAdvancingRef: React.MutableRefObject<boolean>;
  hasStartedRef: React.MutableRefObject<boolean>;
  willAutoContinueRef: React.MutableRefObject<boolean>;
  // Setters
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setEarlierPhrases: React.Dispatch<React.SetStateAction<EarlierPhrase[]>>;
  setParts: React.Dispatch<React.SetStateAction<LessonPart[]>>;
  setContextName: React.Dispatch<React.SetStateAction<string | null>>;
  setContextId: React.Dispatch<React.SetStateAction<string | null>>;
  setLanguageCode: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setRunId: React.Dispatch<React.SetStateAction<string | null>>;
  setStepVersion: React.Dispatch<React.SetStateAction<number | null>>;
  setStepIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setLessonComplete: React.Dispatch<React.SetStateAction<boolean>>;
  setPhraseHints: React.Dispatch<React.SetStateAction<string[] | null>>;
  setAwaitingInput: React.Dispatch<React.SetStateAction<boolean>>;
  setAdvanceFailed: React.Dispatch<React.SetStateAction<boolean>>;
  // Helper methods
  addMessage: (message: Message) => void;
  addMessages: (newMessages: Message[]) => void;
  setMessagesWithCheck: (updater: (prev: Message[]) => Message[]) => void;
  getCurrentMessages: () => Message[];
  clearMessages: () => void;
  reset: () => void;
} => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [earlierPhrases, setEarlierPhrases] = useState<EarlierPhrase[]>([]);
  const [parts, setParts] = useState<LessonPart[]>([]);
  const [contextName, setContextName] = useState<string | null>(null);
  const [contextId, setContextId] = useState<string | null>(null);
  const [languageCode, setLanguageCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [stepVersion, setStepVersion] = useState<number | null>(null);
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const [lessonComplete, setLessonComplete] = useState(false);
  const [phraseHints, setPhraseHints] = useState<string[] | null>(null);
  const [awaitingInput, setAwaitingInput] = useState(false);
  const [advanceFailed, setAdvanceFailed] = useState(false);

  // Refs for managing component lifecycle and preventing duplicate calls
  const isAdvancingRef = useRef(false);
  const hasStartedRef = useRef(false);
  const willAutoContinueRef = useRef(false);

  // Helper methods for state updates
  const addMessage = (message: Message): void => {
    setMessages((prev) => [...prev, message]);
  };

  const addMessages = (newMessages: Message[]): void => {
    setMessages((prev) => [...prev, ...newMessages]);
  };

  const setMessagesWithCheck = (
    updater: (prev: Message[]) => Message[]
  ): void => {
    setMessages((prev) => {
      const next = updater(prev);
      messagesRef.current = next;
      return next;
    });
  };

  // Helper to get current messages (for use in async functions)
  // This uses a ref to track the latest messages value
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const getCurrentMessages = (): Message[] => {
    return messagesRef.current;
  };

  const clearMessages = (): void => {
    setMessages([]);
  };

  const reset = (): void => {
    setMessages([]);
    setEarlierPhrases([]);
    setRunId(null);
    setStepVersion(null);
    setStepIndex(null);
    setParts([]);
    setContextName(null);
    setContextId(null);
    setLanguageCode(null);
    setError(null);
    setLessonComplete(false);
    setPhraseHints(null);
    setAwaitingInput(false);
    setAdvanceFailed(false);
    isAdvancingRef.current = false;
    hasStartedRef.current = false;
    willAutoContinueRef.current = false;
  };

  return {
    // State
    messages,
    earlierPhrases,
    parts,
    contextName,
    contextId,
    languageCode,
    loading,
    processing,
    error,
    runId,
    stepVersion,
    stepIndex,
    lessonComplete,
    phraseHints,
    awaitingInput,
    advanceFailed,
    // Refs
    isAdvancingRef,
    hasStartedRef,
    willAutoContinueRef,
    // Setters
    setMessages,
    setEarlierPhrases,
    setParts,
    setContextName,
    setContextId,
    setLanguageCode,
    setLoading,
    setProcessing,
    setError,
    setRunId,
    setStepVersion,
    setStepIndex,
    setLessonComplete,
    setPhraseHints,
    setAwaitingInput,
    setAdvanceFailed,
    // Helper methods
    addMessage,
    addMessages,
    setMessagesWithCheck,
    getCurrentMessages,
    clearMessages,
    reset,
  };
};
